/**
 * Defaults Service
 * Manages global Kendo defaults configuration with deep path support.
 */

import { utilsService } from "./utils.service";

/**
 * Generic object type for defaults storage
 */
type DefaultsObject = Record<string, unknown>;

/**
 * Service for managing global Kendo defaults configuration.
 * Supports deep path-based setting with automatic object creation.
 */
class DefaultsService {
    /**
     * The defaults storage object
     */
    private defaults: DefaultsObject = {};

    /**
     * Get the current defaults object
     */
    getDefaults(): DefaultsObject {
        return this.defaults;
    }

    /**
     * Set a default value at a given path.
     * Supports dot-separated paths for nested values.
     * Objects are deep extended, primitives are replaced.
     *
     * @param key - Dot-separated path (e.g., "breakpoints", "grid.pager.pageSize")
     * @param value - The value to set at the path
     *
     * @example
     * ```typescript
     * // Set a simple value
     * setDefaults('pageSize', 10);
     *
     * // Set a nested value
     * setDefaults('grid.pager.pageSize', 20);
     *
     * // Deep extend an object
     * setDefaults('breakpoints', { sm: 576, md: 768 });
     * ```
     */
    setDefaults(key: string, value: unknown): void {
        const path = key.split(".");
        let curr: DefaultsObject = this.defaults;

        key = path.pop() as string;

        path.forEach((part) => {
            if (curr[part] === undefined) {
                curr[part] = {};
            }

            curr = curr[part] as DefaultsObject;
        });

        if (value !== null && typeof value === "object" && value.constructor === Object) {
            curr[key] = utilsService.deepExtend({}, curr[key] as object || {}, value as object);
        } else {
            curr[key] = value;
        }
    }

    /**
     * Get a default value at a given path.
     *
     * @param key - Dot-separated path (e.g., "breakpoints", "grid.pager.pageSize")
     * @returns The value at the path, or undefined if not found
     */
    getDefault(key: string): unknown {
        const path = key.split(".");
        let curr: DefaultsObject = this.defaults;

        for (const part of path) {
            if (curr[part] === undefined) {
                return undefined;
            }
            curr = curr[part] as DefaultsObject;
        }

        return curr;
    }
}

export const defaultsService = new DefaultsService();